import { Check, Wind, Thermometer, Zap, SlidersHorizontal } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import FAQ from "@/components/FAQ";
import { Eyebrow, GradientText, PrimaryButton } from "@/components/Brand";
import { Reveal } from "@/lib/motion";
import { IMAGES } from "@/lib/site";

const WHAT_CARDS = [
  { icon: Wind, title: "Single Area", text: "Ideal for individual rooms and open-plan spaces." },
  { icon: Thermometer, title: "Reverse Cycle", text: "Heating and cooling from one system." },
  { icon: Zap, title: "Efficient", text: "Condition only the area being used." },
  { icon: SlidersHorizontal, title: "Flexible", text: "Available in many different capacities." },
];

const BENEFITS = [
  "Straightforward installation",
  "No whole-home ductwork",
  "Individual room control",
  "Quiet modern designs",
  "Heating and cooling",
  "Wide range of capacities",
  "Wi-Fi available on many models",
  "Suitable for bedrooms, offices and living areas",
];

const SIZING_FACTORS = [
  "Room area",
  "Ceiling height",
  "Windows",
  "Insulation",
  "Orientation",
  "Occupancy",
  "Room configuration",
];

const FAQS = [
  { q: "What is reverse-cycle air conditioning?", a: "A reverse-cycle air conditioner can provide both cooling and heating by reversing the refrigeration cycle." },
  { q: "Does a split system need an outdoor unit?", a: "Yes. A conventional split system includes an indoor unit connected to an outdoor condenser." },
  { q: "Can a split system heat a room in winter?", a: "Yes. Reverse-cycle split systems provide both heating and cooling." },
  { q: "How many rooms can one standard split system condition?", a: "A conventional single-head split system is generally designed for a single room or defined open-plan area." },
  { q: "What if I need several rooms conditioned?", a: "A multi-head split system or ducted system may be more appropriate." },
  { q: "Can selected split systems qualify for VEU incentives?", a: "Some approved reverse-cycle systems may qualify depending on the installation, existing equipment and current Victorian Energy Upgrades program requirements." },
];

export default function Split() {
  return (
    <>
      <SEO
        title="Split System Air Conditioning Melbourne | Airconditioning Victoria"
        description="Reverse-cycle split system air conditioning installation for Melbourne homes and properties. Efficient heating and cooling from Airconditioning Victoria."
        path="/split-system-air-conditioning"
      />
      <PageHero
        eyebrow="Split System Air Conditioning"
        titleLines={["Efficient comfort", <GradientText key="g">where you need it.</GradientText>]}
        intro="A wall-mounted split system is a simple and effective way to heat and cool an individual room or open-plan area. With no whole-home ductwork required, split systems provide flexible comfort for bedrooms, living spaces, home offices and more."
        ctaText="Get a Split System Quote"
        image="/assets/split-hero.webp"
        imageAlt="Family relaxing in a living room under a wall-mounted split system air conditioner"
      />

      {/* WHAT IS */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="split-what-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-3xl">
            <Eyebrow>The System</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              What is a split system?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              A split system consists of two primary components: an indoor unit and an outdoor
              condenser. These are connected using refrigerant pipework and electrical connections.
            </p>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              In cooling mode, the system removes heat from the indoor area and transfers it
              outside. Reverse-cycle systems can reverse this process to provide heating.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-[18px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ice">
                    <c.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <h3 className="mt-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedblue">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white py-24 md:py-32" data-testid="split-benefits-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Benefits</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Why choose <GradientText>a split system?</GradientText>
            </h2>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm font-medium text-ink">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-brand">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <PrimaryButton to="/contact" testid="split-benefits-cta">Get a Free Quote</PrimaryButton>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[24px] border border-cardline bg-white shadow-[0_24px_60px_rgba(5,59,117,0.1)]">
              <img
                src="/assets/split-benefits.webp"
                alt="Samsung split system indoor unit, outdoor condenser and remote control"
                className="h-[320px] w-full object-contain p-6 md:h-[460px] md:p-10"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SIZING */}
      <section className="bg-cloud py-24 md:py-32" data-testid="split-sizing-section">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>System Sizing</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Getting the size right matters.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              An incorrectly sized air conditioner may struggle to maintain comfort or operate
              efficiently. Capacity should be selected after considering the specifics of your room
              and property — not square metres alone.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[20px] border border-cardline bg-white p-8">
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-navy">
                Capacity should consider
              </h3>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {SIZING_FACTORS.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm font-medium text-ink">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 md:py-32" data-testid="split-faq-section">
        <div className="mx-auto max-w-4xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Common Questions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Split system FAQs.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <FAQ items={FAQS} testidPrefix="split-faq" />
          </Reveal>
          <Reveal delay={0.15} className="mt-10 text-center">
            <PrimaryButton to="/contact" testid="split-faq-cta">Get a Free Quote</PrimaryButton>
          </Reveal>
        </div>
      </section>

      <FinalCTA
        heading="Ready to heat and cool your space?"
        text="Talk to Airconditioning Victoria about the right split system for your room, layout and budget."
      />
    </>
  );
}
